import type { Role } from "@/api/auth"

// Define the default landing page for each role (first page of their sidebar)
export const roleDefaultPages: Record<Role, string> = {
  admin: "/dashboard/property-manager/dashboard/overview", // Admin gets property manager dashboard
  account_payables: "/dashboard/account-payables/dashboard/overview",
  account_receivables: "/dashboard/account-receivables/dashboard/overview", // First page in sidebar (overview)
  utility_admin: "/dashboard/utility-admin/dashboard/overview",
  property_manager: "/dashboard/property-manager/dashboard/overview",
  company_admin: "/dashboard/company-admin/dashboard/overview",
  accountant: "/dashboard/account-payables/dashboard/overview",
  resident_support: "/dashboard/utility-admin/manage/residents",
  home_owner: "/dashboard/home-owner/dashboard/overview",
  resident: "/dashboard/resident/overview",
  tenant: "/dashboard/resident/overview", // tenant maps to resident
  esg: "/dashboard/esg/usage/energy",
}

// Define which routes are allowed for each role based on their sidebar navigation
export const roleAllowedRoutes: Record<Role, string[]> = {
  admin: [
    "/dashboard/company", // shared company access
    "/dashboard/properties", // shared properties access
    "/dashboard/transactions", // shared transactions access
    "/dashboard/reports", // shared reports access
    "/dashboard/settings", // shared settings
    "/dashboard/add-properties", // shared onboarding
    "/dashboard/link-bills", // shared onboarding
    "/dashboard/account-payables", // account payables access
    "/dashboard/account-receivables", // account receivables access
    "/dashboard/utility-admin", // utility admin access
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/visualize-data", // shared data visualization page
    "/dashboard/asset-viewer", // shared asset viewer page
    "/dashboard/", // allow root dashboard
  ],
  account_payables: [
    "/dashboard/account-payables",
    "/dashboard/company", // shared company pages (funding-history, payments, etc.)
    // SLA Breach Report — page lives under /dashboard/utility-admin/...
    // Exact-path allowance so account_payables can reach it from their
    // sidebar without unlocking the rest of /dashboard/utility-admin/.
    "/dashboard/utility-admin/reports/sla-breach-report",
    "/dashboard/settings", // shared settings
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/ai-tools", // shared AI tools
    "/dashboard/visualize-data", // shared data visualization page
    "/dashboard/asset-viewer", // shared asset viewer page
    "/dashboard/", // allow root dashboard (it will redirect appropriately)
  ],
  account_receivables: [
    "/dashboard/account-receivables",
    "/dashboard/company", // shared company pages (funding-history, payments, etc.)
    // SLA Breach Report — exact-path allowance, same rationale as
    // account_payables above.
    "/dashboard/utility-admin/reports/sla-breach-report",
    "/dashboard/settings", // shared settings
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/ai-tools", // shared AI tools
    "/dashboard/visualize-data", // shared data visualization page
    "/dashboard/asset-viewer", // shared asset viewer page
    "/dashboard/", // allow root dashboard (it will redirect appropriately)
  ],
  accountant: [
    "/dashboard/account-payables",
    "/dashboard/account-receivables",
    "/dashboard/company-admin/manage/properties",
    "/dashboard/company-admin/financials",
    "/dashboard/utility-admin/manage/residents",
    // SLA Breach Report — already had utility-admin/manage/residents
    // surgically allowed; same exact-path pattern for the report.
    "/dashboard/utility-admin/reports/sla-breach-report",
    // Work orders raised on behalf of a resident (same capability as
    // utility_admin). Deliberately a prefix grant so the ticket detail route
    // resolves; the 11 utility-admin category queues are NOT granted.
    "/dashboard/work-orders",
    "/dashboard/company",
    "/dashboard/settings",
    "/dashboard/support",
    "/dashboard/ask-ai",
    "/dashboard/ai-tools",
    "/dashboard/visualize-data",
    "/dashboard/asset-viewer",
    "/dashboard/",
  ],
  resident_support: [
    // Client call-center role — resident list only (login-as is launched
    // from that page; the impersonated session is read-only).
    "/dashboard/utility-admin/manage/residents",
    // Work orders they raise on behalf of residents. Prefix grant, so the
    // detail route is covered too. The queue is scoped server-side to
    // call-center-raised tickets, and internal notes are denied there.
    "/dashboard/work-orders",
    "/dashboard/settings", // shared settings
    "/dashboard/support", // shared support page
    "/dashboard/", // allow root dashboard (it will redirect appropriately)
  ],
  utility_admin: [
    "/dashboard/utility-admin",
    "/dashboard/properties", // access to manage-services for spike report navigation
    "/dashboard/work-orders", // shared work orders access
    "/dashboard/settings", // shared settings
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/ai-tools", // shared AI tools
    "/dashboard/visualize-data", // shared data visualization page
    "/dashboard/asset-viewer", // shared asset viewer page
    "/dashboard/", // allow root dashboard (it will redirect appropriately)
  ],
  property_manager: [
    "/dashboard/property-manager", // Property manager specific pages (dashboard)
    "/dashboard/company",
    "/dashboard/properties",
    "/dashboard/transactions",
    "/dashboard/reports",
    "/dashboard/settings",
    "/dashboard/add-properties", // shared onboarding
    "/dashboard/link-bills", // shared onboarding
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/ai-tools", // shared AI tools
    "/dashboard/visualize-data", // shared data visualization page
    "/dashboard/asset-viewer", // shared asset viewer page
    "/dashboard/", // allow root dashboard (it will redirect appropriately)
  ],
  company_admin: [
    "/dashboard/company-admin", // Company admin specific pages (dashboard)
    "/dashboard/company",
    "/dashboard/properties",
    "/dashboard/transactions",
    "/dashboard/reports",
    "/dashboard/settings", // shared settings
    "/dashboard/add-properties", // shared onboarding
    "/dashboard/link-bills", // shared onboarding
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/ai-tools", // shared AI tools
    "/dashboard/visualize-data", // shared data visualization page
    "/dashboard/asset-viewer", // shared asset viewer page
    "/dashboard/", // allow root dashboard (it will redirect appropriately)
  ],
  home_owner: [
    "/dashboard/home-owner", // home owner specific pages (dashboard)
    "/dashboard/company-admin", // B2C homeowners need access to company-admin/dashboard/overview for "Insights"
    "/dashboard/resident", // resident pages (work orders, billers, etc.)
    "/dashboard/company", // company insights, payments, etc.
    "/dashboard/properties", // manage homes, services, etc.
    "/dashboard/transactions", // transactions: pending-and-cleared, declined, etc.
    "/dashboard/reports", // various reports
    "/dashboard/work-orders", // shared work orders access
    "/dashboard/settings", // shared settings
    "/dashboard/add-properties", // shared onboarding
    "/dashboard/link-bills", // shared onboarding
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/ai-tools", // shared AI tools
    "/dashboard/asset-viewer", // shared asset viewer page
    "/dashboard/", // allow root dashboard (it will redirect appropriately)
  ],
  resident: [
    "/dashboard/resident",
    "/dashboard/company", // company payments, statements, etc.
    "/dashboard/transactions", // transactions: pending-and-cleared, declined, etc.
    "/dashboard/reports", // various reports including statements
    "/dashboard/work-orders", // shared work orders access
    "/dashboard/billers", // shared billers page
    "/dashboard/settings",
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/ai-tools", // shared AI tools
    "/dashboard/asset-viewer", // shared asset viewer page
    "/dashboard/", // root dashboard for residents
  ],
  tenant: [
    "/dashboard/resident", // tenant uses resident pages
    "/dashboard/company", // company payments, statements, etc.
    "/dashboard/transactions", // transactions: pending-and-cleared, declined, etc.
    "/dashboard/reports", // various reports including statements
    "/dashboard/work-orders", // shared work orders access
    "/dashboard/billers", // shared billers page
    "/dashboard/settings",
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/ai-tools", // shared AI tools
    "/dashboard/asset-viewer", // shared asset viewer page
    "/dashboard/", // root dashboard for tenants
  ],
  esg: [
    "/dashboard/esg", // all ESG pages
    "/dashboard/support", // shared support page
    "/dashboard/ask-ai", // shared AI page
    "/dashboard/visualize-data", // shared data visualization page
    "/dashboard/", // root dashboard
  ],
}

/**
 * Get the current user role, respecting dev override in development
 */
export function getCurrentRole(): Role {
  // In development, allow role override for testing
  if (process.env.NODE_ENV === 'development' && typeof window !== 'undefined') {
    const devRoleOverride = localStorage.getItem('dev_role_override');
    if (devRoleOverride && Object.keys(roleDefaultPages).includes(devRoleOverride)) {
      return devRoleOverride as Role;
    }
  }

  // This would normally come from auth store, but we'll handle that in the component
  return 'property_manager'; // fallback
}

/**
 * Get the default landing page for a given role
 */
export function getRoleDefaultPage(role: Role): string {
  return roleDefaultPages[role] || roleDefaultPages.property_manager;
}

/**
 * Check if a route is allowed for a given role
 */
export function isRouteAllowedForRole(pathname: string, role: Role): boolean {
  const allowedRoutes = roleAllowedRoutes[role] || [];

  // Check if the pathname starts with any of the allowed route patterns
  return allowedRoutes.some(allowedRoute => {
    // Handle exact matches and prefix matches
    return pathname === allowedRoute || pathname.startsWith(allowedRoute + '/');
  });
}

/**
 * Check if current pathname should trigger a redirect for the given role
 */
export function shouldRedirectForRole(pathname: string, role: Role): boolean {
  // Always allow the default page for the role
  if (pathname === getRoleDefaultPage(role)) {
    return false;
  }

  // Always allow root dashboard - it has its own redirect logic
  if (pathname === "/dashboard" || pathname === "/dashboard/") {
    return false;
  }

  // Check if route is allowed for this role
  return !isRouteAllowedForRole(pathname, role);
}
